/**
 * Global Wishlist Management
 * Handles wishlist functionality across all pages
 */

(function() {
  'use strict';

  const WISHLIST_KEY = 'shopify_wishlist';
  const WISHLIST_META_KEY = 'shopify_wishlist_meta';

  // ============ STORAGE FUNCTIONS ============

  function getWishlist() {
    try {
      const data = localStorage.getItem(WISHLIST_KEY);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  function saveWishlist(wishlist) {
    try {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(wishlist));
      // Dispatch custom event for other components
      window.dispatchEvent(
        new CustomEvent('wishlist:updated', { detail: { wishlist } })
      );
    } catch (e) {
      console.error('Failed to save wishlist:', e);
    }
  }

  function getWishlistMeta() {
    try {
      const data = localStorage.getItem(WISHLIST_META_KEY);
      const parsed = data ? JSON.parse(data) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  function saveWishlistMeta(meta) {
    try {
      localStorage.setItem(WISHLIST_META_KEY, JSON.stringify(meta));
    } catch (e) {
      console.error('Failed to save wishlist metadata:', e);
    }
  }

  function setWishlistItemMeta(productId, itemMeta) {
    if (!itemMeta || typeof itemMeta !== 'object') return;
    const idStr = String(productId);
    const currentMeta = getWishlistMeta();
    currentMeta[idStr] = Object.assign({}, currentMeta[idStr], itemMeta);
    saveWishlistMeta(currentMeta);
  }

  function removeWishlistItemMeta(productId) {
    const idStr = String(productId);
    const currentMeta = getWishlistMeta();
    delete currentMeta[idStr];
    saveWishlistMeta(currentMeta);
  }

  function isInWishlist(productId) {
    const wishlist = getWishlist();
    return wishlist.includes(String(productId));
  }

  function addToWishlist(productId, productHandle, itemMeta) {
    const wishlist = getWishlist();
    const idStr = String(productId);
    if (!wishlist.includes(idStr)) {
      wishlist.push(idStr);
      saveWishlist(wishlist);
      // Store handle for wishlist page
      const resolvedHandle = productHandle || (itemMeta && itemMeta.handle);
      if (resolvedHandle) localStorage.setItem(`wishlist_handle_${idStr}`, resolvedHandle);
      setWishlistItemMeta(idStr, itemMeta);
      showToast('Added to wishlist');
      return true;
    }
    setWishlistItemMeta(idStr, itemMeta);
    return false;
  }

  function removeFromWishlist(productId) {
    let wishlist = getWishlist();
    const idStr = String(productId);
    wishlist = wishlist.filter((id) => id !== idStr);
    saveWishlist(wishlist);
    localStorage.removeItem(`wishlist_handle_${idStr}`);
    removeWishlistItemMeta(idStr);
    showToast('Removed from wishlist');
    return wishlist;
  }

  // ============ TOAST NOTIFICATION ============

  function showToast(message) {
    // Remove existing toast
    const existingToast = document.querySelector('.wishlist-toast');
    if (existingToast) {
      existingToast.remove();
    }

    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.innerHTML = `
      <span class="mi" aria-hidden="true">favorite</span>
      <span>${message}</span>
    `;
    document.body.appendChild(toast);

    // Trigger animation
    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    // Remove after delay
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  }

  // ============ BUTTON UPDATES ============

  function updateButtonState(button, productId) {
    const isIn = isInWishlist(productId);
    if (isIn) {
      button.classList.add('is-active');
      button.setAttribute('aria-pressed', 'true');
      // Update icon to filled
      const icon = button.querySelector('.mi');
      if (icon) {
        icon.textContent = 'favorite';
        icon.style.fontVariationSettings = "'FILL' 1";
      }
    } else {
      button.classList.remove('is-active');
      button.setAttribute('aria-pressed', 'false');
      // Update icon to outline
      const icon = button.querySelector('.mi');
      if (icon) {
        icon.textContent = 'favorite_border';
        icon.style.fontVariationSettings = '';
      }
    }
  }

  function updateHeaderCount() {
    const wishlist = getWishlist();
    const headerBadge = document.querySelector('[data-wishlist-count]');

    if (headerBadge) {
      if (wishlist.length > 0) {
        headerBadge.textContent = wishlist.length;
        headerBadge.style.display = 'flex';
      } else {
        headerBadge.style.display = 'none';
      }
    }
  }

  function updateAllButtons(productId) {
    document.querySelectorAll(`[data-wishlist-btn][data-product-id="${productId}"], [data-wishlist-btn-secondary][data-product-id="${productId}"]`).forEach((btn) => {
      updateButtonState(btn, productId);
    });
  }

  // ============ EVENT HANDLERS ============

  function handleWishlistClick(e) {
    const button = e.target.closest('[data-wishlist-btn], [data-wishlist-btn-secondary]');
    if (!button) return;

    e.preventDefault();
    e.stopPropagation();

    const productId = button.dataset.productId;
    const productHandle = button.dataset.productHandle;
    const itemMeta = {
      handle: productHandle || '',
      title: button.dataset.productTitle || '',
      url: button.dataset.productUrl || '',
      image: button.dataset.productImage || '',
      price: button.dataset.productPrice || '',
    };

    if (!productId) {
      console.warn('Wishlist button missing product ID');
      return;
    }

    const wasAdded = !isInWishlist(productId);

    if (wasAdded) {
      addToWishlist(productId, productHandle, itemMeta);
    } else {
      removeFromWishlist(productId);
    }

    updateButtonState(button, productId);
    updateAllButtons(productId);
    updateHeaderCount();
  }

  // ============ INITIALIZATION ============

  function initWishlistButtons() {
    // Initialize all wishlist buttons on the page
    document.querySelectorAll('[data-wishlist-btn], [data-wishlist-btn-secondary]').forEach((button) => {
      const productId = button.dataset.productId;
      if (productId) {
        updateButtonState(button, productId);
      }
    });

    // Update header count
    updateHeaderCount();

    // Add event delegation for wishlist buttons
    document.removeEventListener('click', handleWishlistClick);
    document.addEventListener('click', handleWishlistClick);
  }

  // Listen for wishlist updates from other components
  window.addEventListener('wishlist:updated', function() {
    document.querySelectorAll('[data-wishlist-btn], [data-wishlist-btn-secondary]').forEach((button) => {
      const productId = button.dataset.productId;
      if (productId) {
        updateButtonState(button, productId);
      }
    });
    updateHeaderCount();
  });

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initWishlistButtons);
  } else {
    initWishlistButtons();
  }

  // Re-initialize when new content is added (for AJAX cart, etc.)
  document.addEventListener('shopify:section:load', initWishlistButtons);

  // Expose functions globally
  window.Wishlist = {
    get: getWishlist,
    getMeta: getWishlistMeta,
    add: addToWishlist,
    remove: removeFromWishlist,
    isIn: isInWishlist,
    updateCount: updateHeaderCount
  };

})();
